import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Tabs,
  Tab,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon
} from '@mui/icons-material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import AdminUsersApiService from '../services/adminUsersApi';
import ChangePasswordDialog from './auth/ChangePasswordDialog';
import ForgotPasswordDialog from './auth/ForgotPasswordDialog';
import SignupForm from './auth/SignupForm';
import { errorField } from '../lib/http';
import { logger } from '../lib/logger';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { loading, signInWithGoogle, isAuthenticated, user } = useAuth();

  // État pour la connexion email/password
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  // État pour le changement de mot de passe
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [checkingPassword, setCheckingPassword] = useState(false);

  // État pour le mot de passe oublié
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  // Vérifier si le changement de mot de passe est requis
  useEffect(() => {
    const checkPasswordChangeRequired = async () => {
      if (isAuthenticated && user) {
        setCheckingPassword(true);
        try {
          // Profil du compte connecté (/admin/users/me, ouvert au lecteur) : la fiche
          // /api/v1/admin/users/<uid> est réservée aux admins (403 pour un lecteur).
          const response = await AdminUsersApiService.getMyProfile();
          if (response.success && response.data) {
            const requiresChange = response.data.metadata?.passwordChangeRequired === true;
            setPasswordChangeRequired(requiresChange);
          }
        } catch (error) {
          logger.error('Erreur lors de la vérification du mot de passe:', error);
        } finally {
          setCheckingPassword(false);
        }
      }
    };

    checkPasswordChangeRequired();
  }, [isAuthenticated, user]);

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoggingIn(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // La connexion réussie sera gérée par useAuth
    } catch (error: unknown) {
      logger.error('Erreur de connexion:', error);

      if (errorField(error, 'code') === 'auth/invalid-credential' || errorField(error, 'code') === 'auth/wrong-password') {
        setLoginError('Email ou mot de passe incorrect');
      } else if (errorField(error, 'code') === 'auth/user-not-found') {
        setLoginError('Aucun compte trouvé avec cet email');
      } else if (errorField(error, 'code') === 'auth/too-many-requests') {
        setLoginError('Trop de tentatives. Veuillez réessayer plus tard.');
      } else {
        setLoginError('Erreur de connexion. Veuillez réessayer.');
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const handlePasswordChanged = () => {
    setPasswordChangeRequired(false);
  };

  if (loading || checkingPassword) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              Suklu Academy
            </Typography>
            <Typography variant="h6" color="textSecondary" gutterBottom textAlign="center">
              Administration
            </Typography>

            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3, mt: 2 }}>
              <Tab label="Email / Mot de passe" />
              <Tab label="Google" />
              <Tab label="Inscription" />
            </Tabs>

            {/* Onglet Email/Password */}
            {tabValue === 0 && (
              <Box component="form" onSubmit={handleEmailPasswordLogin}>
                {loginError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoginError(null)}>
                    {loginError}
                  </Alert>
                )}

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                  autoComplete="email"
                />

                <TextField
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 3 }}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loggingIn ? <CircularProgress size={20} /> : <LoginIcon />}
                  disabled={loggingIn}
                  size="large"
                  fullWidth
                >
                  Se connecter
                </Button>

                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setForgotPasswordOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Mot de passe oublié ?
                  </Button>
                </Box>
              </Box>
            )}

            {/* Onglet Google */}
            {tabValue === 1 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 3 }} textAlign="center">
                  Connectez-vous avec votre compte Gmail
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<GoogleIcon />}
                  onClick={signInWithGoogle}
                  size="large"
                  fullWidth
                >
                  Se connecter avec Google
                </Button>
              </Box>
            )}

            {/* Onglet Inscription */}
            {tabValue === 2 && (
              <SignupForm
                onSuccess={() => {
                  // Rediriger vers l'onglet login après inscription
                  setTabValue(0);
                }}
                onSwitchToLogin={() => setTabValue(0)}
              />
            )}
          </Paper>
        </Box>
      </Container>

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />
      </>
    );
  }

  // Afficher le dialog de changement de mot de passe si requis
  if (passwordChangeRequired) {
    return (
      <>
        <ChangePasswordDialog
          open={passwordChangeRequired}
          onPasswordChanged={handlePasswordChanged}
          required={true}
        />
        {/* Afficher l'app en arrière-plan mais grisée */}
        <Box sx={{ filter: 'blur(5px)', pointerEvents: 'none' }}>
          {children}
        </Box>
      </>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;